// Partner credentials and runtime context belong to this browser tab.
// Legacy single-restaurant sessions can still be remembered across browser restarts.
const TAB_MARKER = 'ewu_partner_tab_session_v1';
const memory = new Map();
let switching = false;

function read(storage, key) {
  try { return storage()?.getItem(key) ?? null; } catch { return null; }
}
function write(storage, key, value) {
  try { storage()?.setItem(key, value ?? 'null'); } catch { /* Memory still works. */ }
}
const tab = () => typeof sessionStorage !== 'undefined' ? sessionStorage : null;
const shared = () => typeof localStorage !== 'undefined' ? localStorage : null;
const valid = value => value && !['null', 'undefined'].includes(String(value)) ? value : null;

export function partnerTokenClaims(token = getPartnerToken()) {
  try {
    const encoded = String(token || '').split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(encoded));
  } catch { return {}; }
}

function initializeTab() {
  if (read(tab, TAB_MARKER) || memory.has(TAB_MARKER)) return;
  for (const key of ['partner_token', 'refresh']) {
    const existing = read(tab, key);
    const value = existing !== null ? existing : read(shared, key);
    memory.set(key, value);
    write(tab, key, value);
  }
  memory.set(TAB_MARKER, '1');
  write(tab, TAB_MARKER, '1');
}
export function readPartnerSessionValue(key) {
  initializeTab();
  const value = read(tab, key);
  return valid(value !== null ? value : memory.get(key));
}
export const getPartnerToken = () => readPartnerSessionValue('partner_token');
export const getPartnerRefresh = () => readPartnerSessionValue('refresh');

export function setPartnerSession(data) {
  initializeTab();
  const before = partnerTokenClaims();
  const after = data.access ? partnerTokenClaims(data.access) : before;
  if (before.id !== after.id || before.restaurant_id !== after.restaurant_id || after.token_type === 'partner_selection') {
    for (const key of ['ewu_tpv_partner_session_cache', 'ewu_device_operational_policy_v1', 'ewu_operational_authority_v1', 'ewu_tpv_active_restaurant_id', 'app_state:is_waiter_mode', 'app_state:restaurant_customs', 'restaurant_primary_color', 'restaurant_secondary_color']) clearTabContext(key);
  }
  if (data.access) {
    memory.set('partner_token', data.access);
    write(tab, 'partner_token', data.access);
    const claims = partnerTokenClaims(data.access);
    if (!claims.restaurant_id && !claims.token_type) {
      write(shared, 'partner_token', data.access);
      if (data.refresh) write(shared, 'refresh', data.refresh);
    }
  }
  if (data.refresh) {
    memory.set('refresh', data.refresh);
    write(tab, 'refresh', data.refresh);
  }
  if (typeof window !== 'undefined') window.dispatchEvent(new Event('ewu-partner-session-changed'));
}

export function clearPartnerSession() {
  const current = partnerTokenClaims();
  const remembered = partnerTokenClaims(read(shared, 'partner_token'));
  if (current.id && current.id === remembered.id) {
    try { shared()?.removeItem('partner_token'); shared()?.removeItem('refresh'); } catch { /* Optional persistence. */ }
  }
  for (const key of ['partner_token', 'refresh']) {
    memory.set(key, null);
    write(tab, key, null);
  }
  // Retain the marker so this tab never imports another tab's remembered login.
}

export function readTabContext(key) {
  initializeTab();
  const value = read(tab, key);
  if (value !== null) return valid(value);
  if (memory.has(key)) return valid(memory.get(key));
  // Only old, unscoped sessions may import their existing runtime cache.
  if (!partnerTokenClaims().restaurant_id) {
    const legacy = read(shared, key);
    if (legacy !== null) { writeTabContext(key, legacy); return valid(legacy); }
  }
  return null;
}
export function writeTabContext(key, value) {
  memory.set(key, value);
  write(tab, key, value);
}
export function clearTabContext(key) {
  memory.set(key, null);
  write(tab, key, null);
}
export function getPartnerRestaurantId() {
  const claims = partnerTokenClaims();
  if (claims.restaurant_id) return String(claims.restaurant_id);
  try {
    const profile = JSON.parse(readTabContext('ewu_tpv_partner_session_cache') || 'null');
    return profile && profile.id === claims.id ? String(profile.restaurant_id || '') : '';
  } catch { return ''; }
}
export function isPartnerSessionSwitching() { return switching; }
export function setPartnerSessionSwitching(value) { switching = Boolean(value); }
